import { type CountryCode, type ProjectCode } from '@constants'
import { useApiCampaignAvailableCoupons } from '@hooks-api/fortune/useApiCampaignAvailableCoupons'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type Coupon, type CouponType } from '@hooks-api/types/dataModel/coupon'
import { useCallback, useMemo } from 'react'
import * as couponService from '../../core/couponService'
import { parseAppliedCouponCriterionsMap } from '../../parser/parseAppliedCouponCriterionsMap'
import { parseAvailableCouponCriterionGiveawayProductIds } from '../../parser/parseAvailableCouponCriterionGiveawayProductIds'
import { parseMergedCouponMap } from '../../parser/parseMergedCouponMap'
import { cartStoreMap, type PlatformType } from './store-map'
import { useFpB2bCartCalculate } from './use-fp-b2b-cart-calculate'
import { useFpB2bProductsInfo } from './use-fp-b2b-products-info'

export const useFpB2bCoupons = (campaignId: number, projectCode: ProjectCode, countryCode: CountryCode, campaignCode: string | undefined, platform: PlatformType) => {
  const { useCartStore } = cartStoreMap[platform]
  const [cartValues] = useCartStore(campaignId)
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isShopManager = userInfo?.roles.includes('shop_manager')
  const { destructuredData: availableCoupons } = useApiCampaignAvailableCoupons({
    campaign_code: campaignCode,
    project_code: projectCode,
    merchant_id: isShopManager ? cartValues.merchant.index : null,
    manual_input_coupon_ids: cartValues.coupon.manualInputCouponIds.join(','),
  })

  const { calculateResult } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const giveawayProductIds = useMemo(() => parseAvailableCouponCriterionGiveawayProductIds(availableCoupons), [availableCoupons])
  const { productMap } = useFpB2bProductsInfo({
    campaignId,
    projectCode,
    countryCode,
    campaignCode,
    extraProductIds: giveawayProductIds,
    platform,
  })

  const appliedCouponCriterionsMap = useMemo(() => parseAppliedCouponCriterionsMap(calculateResult?.applied_coupons),
    [calculateResult])

  const mergedCouponMap = useMemo(() => parseMergedCouponMap(availableCoupons, appliedCouponCriterionsMap, productMap),
    [appliedCouponCriterionsMap, availableCoupons, productMap])

  const getMergedCouponCriterions = useCallback((couponIds: Coupon['id'][], types?: CouponType[]) => {
    if (!mergedCouponMap) return []
    return couponService.getMergedCouponCriterions(mergedCouponMap, couponIds, types)
  }, [mergedCouponMap])

  const availableMergedCoupons = useMemo(() => mergedCouponMap ? Array.from(mergedCouponMap.values()) : undefined, [mergedCouponMap])

  return {
    availableMergedCoupons,
    mergedCouponMap,
    getMergedCouponCriterions,
    calculateResult,
  }
}
